import type { ReactNode } from 'react';
import { useEffect, useId, useRef } from 'react';

import { cn } from '@/lib/cn';

import { Icon } from './Icon';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** The row of buttons along the bottom. Primary action last. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  children?: ReactNode;
}

/**
 * Built on the native `<dialog>` element.
 *
 * That gets focus trapping, the top layer, inertness of the page behind and
 * Escape-to-close from the platform instead of from three hundred lines of
 * our own. What is left for us is: routing the browser's `cancel`/`close`
 * events back into React state, and the styling.
 *
 * On a phone it is a sheet anchored to the bottom, because that is where
 * thumbs are. On a wide screen it is centred.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  footer,
  size = 'md',
  children,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    if (open && !node.open) node.showModal();
    if (!open && node.open) node.close();
  }, [open]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // `cancel` is Escape; `close` also covers form method="dialog" submits.
    const handleClose = () => onClose();
    node.addEventListener('close', handleClose);
    return () => node.removeEventListener('close', handleClose);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      className={cn(
        'w-full max-w-[calc(100vw-2*var(--gutter))] bg-transparent p-0 text-ink backdrop:bg-ink/45 backdrop:backdrop-blur-[2px]',
        // Bottom sheet on a phone, centred card from `sm` up.
        'mb-0 mt-auto sm:m-auto',
        size === 'sm' && 'sm:max-w-md',
        size === 'md' && 'sm:max-w-xl',
        size === 'lg' && 'sm:max-w-3xl',
      )}
      onClick={(event) => {
        // A click on the backdrop lands on the dialog element itself.
        if (event.target === ref.current) onClose();
      }}
    >
      <div
        className={cn(
          'flex max-h-[85dvh] flex-col overflow-hidden border border-line bg-surface shadow-modal',
          'rounded-t-xl sm:rounded-xl',
        )}
      >
        <header className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="font-display text-lead font-semibold tracking-tight">
              {title}
            </h2>
            {description ? <p className="mt-1 text-small text-muted">{description}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 -mt-1 grid size-8 shrink-0 place-items-center rounded-md text-subtle transition-colors hover:bg-sunken hover:text-ink"
          >
            <Icon name="close" size={16} />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-paper px-5 py-3">
            {footer}
          </footer>
        ) : null}
      </div>
    </dialog>
  );
}
